import React from 'react';
import clsx from 'clsx';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { BsTrash3, BsCaretDownFill, BsHouseHeartFill, BsArchive, BsArrowCounterclockwise } from 'react-icons/bs';
import { MdOutlineModeEditOutline } from 'react-icons/md';

import type { TranslationFunction } from '../types/common';
import BayTooltip from '../BayTooltip';
import VisibilityIcon from '../Icons/VisibilityIcon';

import './BayBulkActions.scss';

export interface BulkAction {
	id: string;
	label: string;
	disabled?: boolean;
	showButton?: boolean;
	// ReactNode, not string: a disabled reason may need inline emphasis (e.g. the italic
	// product name in `sharedArchiveManagedInOffice`). Every consumer below passes this
	// straight to `BayTooltip`'s `overlay`, which is already ReactNode, or tests it for
	// truthiness — so widening is backward compatible.
	tooltip?: React.ReactNode;
	actingAsAccessLevel?: string;
}

export interface BayBulkActionsProps {
	selectedData?: Array<Record<string, any>>;
	hideBulkActions: () => void;
	onActionClick: (actionId: string, selectedData: Array<Record<string, any>>) => void;
	bulkActions?: BulkAction[];
	isLoading?: boolean;
	translate?: TranslationFunction;
	t?: TranslationFunction;
	dropdownClassName?: string;
	className?: string;
	isEditDisabled?: boolean;
	enableNewUI?: boolean;
	showAllSelectOption?: boolean;
	totalItemsCount?: number;
	totalSelectedItems?: number;
	selectedAllData?: boolean;
	setSelectedAllData?: (value: boolean) => void;
	mobileActionsLabel?: string;
}

const CustomToggle = React.forwardRef<HTMLDivElement, { children: React.ReactNode; onClick: (e: React.MouseEvent) => void }>(({ children, onClick }, ref) => (
	<div
		ref={ref}
		className="d-flex align-items-center btn btn-outline-secondary more-actions-toggle justify-content-center fw-normal"
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
	>
		{children}
		<BsCaretDownFill className="more-actions-toggle-icon ms-1" />
	</div>
));

CustomToggle.displayName = 'CustomToggle';

const BayBulkActions: React.FC<BayBulkActionsProps> = ({
	selectedData = [],
	hideBulkActions,
	onActionClick,
	bulkActions = [],
	isLoading,
	translate,
	t,
	dropdownClassName = '',
	className = '',
	isEditDisabled = false,
	enableNewUI = false, // this is prop is used to display the updated selected records design
	showAllSelectOption = false,
	totalItemsCount = 0,
	totalSelectedItems,
	selectedAllData = false,
	setSelectedAllData,
	mobileActionsLabel = 'More Actions', // mobile bulk action dropdown menu button text
}) => {
	const innerTranslate = useTranslation();
	const translateFunc: TranslationFunction = t || translate || innerTranslate.t;
	// In global-selection mode, display counts against the full filtered dataset, not just current page rows.
	const selectedCountValue = selectedAllData
		? totalItemsCount
		: typeof totalSelectedItems === 'number'
			? totalSelectedItems
			: selectedData?.length || 0;
	const selectedCount = selectedAllData
		? selectedCountValue > 0
			? new Intl.NumberFormat('en-US').format(selectedCountValue)
			: 0
		: selectedCountValue;
	const hasSharedContactDelete =
		selectedData?.some(
			(item) =>
				item?.isSharedContact === 'true' && item?.allowdelete === 'false',
		) || false;
	const isLeadType =
		selectedData?.some((item) => item?.leadtype?.toLowerCase() === 'a') ||
		false;

	const classes = clsx(
		'bay-bulk-actions my-3 py-2',
		{
			'bay-placeholder-overlay': isLoading,
		},
		className,
	);

	return (
		<div className={classes}>
			<div className="row g-0 justify-content-between w-100">
				<div className="d-flex align-items-center justify-content-between justify-content-sm-start">
					{enableNewUI ? (
						<div className="text-500">
							{translateFunc(selectedCountValue === 1 ? 'selectedItemCount' : 'selectedItemsCount', { count: selectedCount })}
						</div>
					) : (
						<div className="bay-selection-counter me-2 me-sm-4">
							{selectedCount}
						</div>
					)}
					{showAllSelectOption && (
						<>
							<span className="vertical-divider border-200 mx-3"></span>
							<div className="text-primary me-4 cursor-pointer" onClick={() => setSelectedAllData(!selectedAllData)}>
								{/* Toggle between global filtered selection and standard row selection mode. */}
								{selectedAllData ? (
									translateFunc('clearSelection')
								) : (
									translateFunc('bulkSelectAllText', { count: totalItemsCount > 0 ? new Intl.NumberFormat('en-US').format(totalItemsCount) : 0 })
								)}
							</div>
						</>
					)}
					{bulkActions?.map((item, index) => {
						if (item?.showButton) {
							switch (item.id) {
							case 'delete':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip
											overlay={
												item?.tooltip
													? item.tooltip
													: item?.disabled &&
															  isLeadType &&
															  item?.actingAsAccessLevel?.toLowerCase() === 'a'
														? translateFunc('MixedSelectionAdminTooltipMsg')
														: item?.disabled &&
																  isLeadType &&
																  item?.actingAsAccessLevel?.toLowerCase() === 'u'
															? translateFunc('MixedSelectionUserTooltipMsg')
															: hasSharedContactDelete
																? translateFunc('deleteBulkActionSharedContactTooltip')
																: translateFunc('delete')
											}
											placement="top"
											overlayClassName=""
											inverseStyle={false}
										>
											<span>
												<Button
													key={index}
													className={`filter-btn ${bulkActions.some((e) => e.id === 'visibility') ? 'me-1' : 'me-2 me-sm-4'}`}
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													<BsTrash3 />
												</Button>
											</span>
										</BayTooltip>
									</div>
								);
							case 'visibility':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip overlay="Visibility" placement="top" overlayClassName="" inverseStyle={false}>
											<Button
												key={index}
												className="filter-btn ms-1 me-2 me-sm-4"
												disabled={item.disabled}
												variant="outline-secondary"
												onClick={() => onActionClick(item.id, selectedData)}
											>
												<VisibilityIcon
													isHidden={selectedData.some(
														(data) => data.isHidden,
													)}
												/>
											</Button>
										</BayTooltip>
									</div>
								);
							case 'homepage':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip overlay="On Homepage" placement="top" overlayClassName="" inverseStyle={false}>
											<Button
												key={index}
												className="filter-btn ms-1 me-2 me-sm-4"
												disabled={item.disabled}
												variant="outline-secondary"
												onClick={() => onActionClick(item.id, selectedData)}
											>
												<BsHouseHeartFill />
											</Button>
										</BayTooltip>
									</div>
								);
							case 'archive':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip
											overlay={
												item?.tooltip || translateFunc('archive')
											}
											placement="top"
											overlayClassName=""
											inverseStyle={false}
										>
											<span>
												<Button
													key={index}
													className={`filter-btn ${bulkActions.some((e) => e.id === 'visibility') ? 'me-1' : 'me-2 me-sm-4'}`}
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													<BsArchive />
												</Button>
											</span>
										</BayTooltip>
									</div>
								);
							case 'restore':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip
											overlay={
												item?.tooltip || translateFunc('restore')
											}
											placement="top"
											overlayClassName=""
											inverseStyle={false}
										>
											<span>
												<Button
													key={index}
													className={`filter-btn ${bulkActions.some((e) => e.id === 'visibility') ? 'me-1' : 'me-2 me-sm-4'}`}
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													<BsArrowCounterclockwise />
												</Button>
											</span>
										</BayTooltip>
									</div>
								);
							case 'edit':
								if (selectedData.length === 1) {
									return (
										<div key={index} className="d-none d-sm-block">
											<BayTooltip overlay={ item?.tooltip ?? (isEditDisabled ? translateFunc('disableListingEdit') : translateFunc('edit')) } placement="top" overlayClassName="" inverseStyle={false}>
												<span>
													<Button
														key={index}
														className="filter-btn me-2"
														variant="outline-secondary"
														disabled={item.disabled || isEditDisabled }
														onClick={() => onActionClick(item.id, selectedData)}
													>
														<MdOutlineModeEditOutline size={17} />
													</Button>
												</span>
											</BayTooltip>
										</div>
									);
								}
								return null;
							case 'status':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip overlay={item?.tooltip || translateFunc('changeStatus')} placement="top" overlayClassName="" inverseStyle={false}>
											<span>
												<Button
													key={index}
													className="filter-btn ms-1 me-2"
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													{item.label}
												</Button>
											</span>
										</BayTooltip>
									</div>
								);
							case 'category':
								return (
									<div key={index} className="d-none d-sm-block">
										<BayTooltip overlay={item?.tooltip || translateFunc('changeCategory')} placement="top" overlayClassName="" inverseStyle={false}>
											<span>
												<Button
													key={index}
													className="filter-btn ms-1 me-2 me-sm-4"
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													{item.label}
												</Button>
											</span>
										</BayTooltip>
									</div>
								);
							default:
								return (
									<span key={index}>
										<BayTooltip
											disabled={
												!item.disabled ||
													(item.disabled &&
														(item.id === 'sendEmail' ||
															item.id !== 'addToCampaign'))
											}
											overlay={
												item.id === 'addToCampaign' && item.disabled
													? translateFunc('BulkAssignCampaignButtonTooltip')
													: null
											}
											placement="top"
											overlayClassName=""
											inverseStyle={false}
										>
											<span>
												<Button
													key={index}
													className="filter-btn me-2 col-md-auto d-none d-lg-inline-block"
													disabled={item.disabled}
													variant="outline-secondary"
													onClick={() => onActionClick(item.id, selectedData)}
												>
													{item.label}
												</Button>
											</span>
										</BayTooltip>
									</span>
								);
							}
						}
						return null;
					})}
					<div className="d-block d-lg-none flex-grow-1 mx-4">
						<Dropdown
							className={`row g-0 ${dropdownClassName}`}
							onSelect={(eventKey) => eventKey && onActionClick(eventKey, selectedData)}
						>
							<Dropdown.Toggle as={CustomToggle} variant="outline-secondary">
								{mobileActionsLabel}
							</Dropdown.Toggle>
							<Dropdown.Menu renderOnMount={false}>
								{bulkActions?.map((action, index) => {
									if (action.showButton) {
										if (
											(action.id === 'edit' && selectedData.length < 2) ||
											action.id === 'delete' ||
											action.id === 'archive' ||
											action.id === 'restore'
										) {
											return (
												<Dropdown.Item
													key={index}
													className={`${action.disabled ? 'disabled' : ''} d-block d-sm-none`}
													disabled={action.id === 'edit' ? (action.disabled || isEditDisabled) : action.disabled}
													eventKey={action.id}
												>
													<span className="text-wrap">{action.label}</span>
												</Dropdown.Item>
											);
										} else {
											return (
												action.id !== 'edit' && (
													<Dropdown.Item
														key={index}
														className={`${action.disabled ? 'disabled' : ''}`}
														disabled={action.disabled}
														eventKey={action.id}
													>
														<span className="text-wrap">{action.label}</span>
													</Dropdown.Item>
												)
											);
										}
									}
									return null;
								})}
							</Dropdown.Menu>
						</Dropdown>
					</div>
					<div className="ms-sm-auto">
						<Button
							className="text-decoration-none fw-bold"
							variant="link"
							onClick={() => hideBulkActions()}
						>
							Close
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BayBulkActions;
